import { Component } from 'preact';
import {
  GOLD,
  TEXTURED_PANEL_STYLE,
  CONTENT_BG_DEFAULT,
  MedievalBtn,
  FourCorners,
  ACCENT_GOLD,
} from '../../lib/medievalTheme';
import { t } from '../../localization/t';

// Fully opaque variant of TEXTURED_PANEL_STYLE. Same recipe as the
// AudioMixerPopover (custom music menu). PANEL_BG is rgba(…, 0.92)
// which is fine for centered modals over a darkening scrim, but as a
// top-of-screen banner over a busy battle board it would still
// expose the gameplay layer through it. Replacing the alpha base
// with a fully opaque dark colour keeps the carved-stone-tablet feel
// while making the panel a true UI surface.
const OPAQUE_PANEL_BG = '#0e0c08';
const OPAQUE_TEXTURED_PANEL_STYLE = {
  ...TEXTURED_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, ${OPAQUE_PANEL_BG}`,
};

/**
 * Pre-game ceremony banner shown while a Harbinger avatar divines
 * its three marked squares. Uses the same opaque textured panel
 * recipe as the AudioMixerPopover so it reads as a real medieval UI
 * surface. Never transparent. With stone/chisel/scratches/cracks
 * textures over a fully opaque dark base, a saturated gold rim, and
 * gold L-corner plating that hugs the actual borderRadius.
 *
 * Lifecycle (driven by props.phase):
 *   - 'rolling'         → indeterminate caption while the slot-machine sweep runs
 *   - 'pending_confirm' → Accept / Cancel buttons surfaced
 *   - 'done'            → parent removes us
 */
export default class HarbingerSetupOverlay extends Component {
  render() {
    const {
      playerLabel,
      ownedByLocal,
      // Whether THIS client answers the prompt. Equals ownedByLocal online; the
      // solo pilot decides for both seats while the title still names the seat.
      canDecide = ownedByLocal,
      portraitUrl,
      phase = 'rolling',
      onAccept,
      onCancel,
    } = this.props;

    const isPending = phase === 'pending_confirm';
    const caption = isPending
      ? (canDecide
        ? t('Accept these omens, or refuse them.')
        : t('Awaiting their decision.'))
      : t('Divining three squares of the realm.');

    const titlePrefix = ownedByLocal ? t('Your Harbinger') : t("{name}'s Harbinger", { name: playerLabel });

    return (
      <div style={shellStyle} aria-live="polite">
        <style>{KEYFRAMES}</style>
        <div style={panelStyle}>
          {/* Gold L-corner plates flush with the panel's actual
              borderRadius. radius is passed as a CSS string so it
              matches the panel curve exactly; inset=0 sits the L
              outer edge ON the rim. */}
          <FourCorners color={`${GOLD} 0.55)`} inset={0} />
          <div style={portraitFrameStyle}>
            {portraitUrl ? (
              <img
                src={portraitUrl}
                alt={t('Harbinger')}
                style={portraitImgStyle}
                draggable={false}
              />
            ) : (
              <div style={{ ...portraitImgStyle, background: '#1a0f06' }} />
            )}
            <div style={portraitVignetteStyle} />
            <div style={portraitGlowStyle} />
          </div>
          <div style={textColStyle}>
            <div style={titleStyle}>{titlePrefix}</div>
            <div style={dividerStyle} />
            <div style={captionStyle}>{caption}</div>
          </div>
          {isPending && canDecide && (
            <div style={buttonsStyle}>
              <MedievalBtn
                variant="danger"
                sound="none"
                onClick={onCancel}
                style={btnSizeStyle}
              >
                {t('Cancel')}
              </MedievalBtn>
              <MedievalBtn
                variant="gold"
                sound="none"
                onClick={onAccept}
                style={btnSizeStyle}
              >
                {t('Accept')}
              </MedievalBtn>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const shellStyle = {
  position: 'fixed',
  top: '3cqw',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 2000,
  pointerEvents: 'none',
  animation: 'harbingerBannerIn 320ms ease-out forwards',
};

const panelStyle = {
  ...OPAQUE_TEXTURED_PANEL_STYLE,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '1.4cqw',
  padding: '1.1cqw 2.0cqw 1.1cqw 1.3cqw',
  borderRadius: 'var(--radius-xl)',
  pointerEvents: 'auto',
};

const portraitFrameStyle = {
  position: 'relative',
  width: '5.6cqw',
  height: '5.6cqw',
  borderRadius: '50%',
  overflow: 'hidden',
  border: `2px solid ${ACCENT_GOLD}`,
  boxShadow: [
    '0 0 32px rgba(255, 192, 96, 0.45)',
    'inset 0 0 18px rgba(0,0,0,0.6)',
    'inset 0 0 0 1px rgba(255,235,180,0.35)',
  ].join(', '),
  flexShrink: 0,
};

const portraitImgStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center 22%',
  filter: 'saturate(1.18) contrast(1.06)',
};

const portraitVignetteStyle = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  boxShadow: 'inset 0 0 18px rgba(0,0,0,0.65), inset 0 0 4px rgba(0,0,0,0.7)',
  borderRadius: '50%',
};

const portraitGlowStyle = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  background: 'radial-gradient(circle at center, rgba(255,210,140,0.32) 0%, rgba(0,0,0,0) 70%)',
  animation: 'harbingerBreath 2200ms ease-in-out infinite',
  borderRadius: '50%',
};

const textColStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35cqw',
  minWidth: '22cqw',
};

const titleStyle = {
  fontFamily: 'IM Fell English SC, serif',
  fontSize: '1.75cqw',
  letterSpacing: '0.06em',
  color: '#f6e0b3',
  textShadow: '0 0 14px rgba(255, 180, 90, 0.5), 0 2px 0 #2a1606',
  whiteSpace: 'nowrap',
};

const dividerStyle = {
  height: '1px',
  background: `linear-gradient(90deg, transparent 0%, ${ACCENT_GOLD}99 50%, transparent 100%)`,
  width: '100%',
};

const captionStyle = {
  fontFamily: 'IM Fell English, serif',
  fontSize: '1.05cqw',
  color: '#d8c7a4',
  fontStyle: 'italic',
  textShadow: '0 1px 0 rgba(0,0,0,0.8)',
  animation: 'harbingerCaptionPulse 1800ms ease-in-out infinite',
  whiteSpace: 'nowrap',
};

const buttonsStyle = {
  display: 'flex',
  gap: '0.7cqw',
  marginLeft: '0.8cqw',
};

const btnSizeStyle = {
  fontFamily: 'IM Fell English SC, serif',
  fontSize: '1.05cqw',
  letterSpacing: '0.06em',
  padding: '0.7cqw 1.6cqw',
  minWidth: '7cqw',
};

const KEYFRAMES = `
  @keyframes harbingerBannerIn {
    from { opacity: 0; transform: translate(-50%, -1.5cqw); }
    to   { opacity: 1; transform: translate(-50%, 0); }
  }
  @keyframes harbingerBreath {
    0%, 100% { opacity: 0.55; transform: scale(1); }
    50%      { opacity: 1.0;  transform: scale(1.06); }
  }
  @keyframes harbingerCaptionPulse {
    0%, 100% { opacity: 0.85; }
    50%      { opacity: 1.0; }
  }
`;
